import { Search, ArrowLeft, BookOpen, Cpu } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function NotFound() {
  useSEO({
    title: '404 - Page Not Found | GreenTick AI Research Lab',
    description: 'The page you are looking for has been moved or archived. Explore our latest technical research papers and hiring insights.',
    canonicalPath: '/404'
  });

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center pt-32 pb-20 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-grid-pattern opacity-40 pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            {/* Glitch-style 404 */}
            <div className="relative inline-block mb-8">
              <span className="text-[12rem] font-black text-white/5 leading-none select-none tracking-tighter">404</span>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 bg-teal-500/20 rounded-3xl flex items-center justify-center border border-teal-500/30 backdrop-blur-xl rotate-12 group hover:rotate-0 transition-transform duration-500">
                  <Search className="w-10 h-10 text-teal-400 -rotate-12 group-hover:rotate-0 transition-transform duration-500" />
                </div>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase tracking-tight">
              Logic <span className="text-teal-400">Terminated</span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-12 leading-relaxed max-w-xl mx-auto">
              The neural path you've requested does not exist in our current index. It may have been re-calibrated or moved to our research archives.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-12">
              <Link 
                to="/research"
                className="glass p-6 rounded-2xl border border-white/5 hover:border-teal-500/40 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center mb-4 border border-teal-500/20 group-hover:bg-teal-500/20 transition-all">
                  <Cpu className="w-5 h-5 text-teal-400" />
                </div>
                <h3 className="text-white font-bold mb-1 uppercase text-sm tracking-wider">Research Lab</h3>
                <p className="text-slate-400 text-xs">Explore our latest technical papers.</p>
              </Link>

              <Link 
                to="/blog"
                className="glass p-6 rounded-2xl border border-white/5 hover:border-emerald-500/40 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 border border-emerald-500/20 group-hover:bg-emerald-500/20 transition-all">
                  <BookOpen className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-white font-bold mb-1 uppercase text-sm tracking-wider">The GreenTick Review</h3>
                <p className="text-slate-400 text-xs">Industry leading AI insights.</p>
              </Link>
            </div>

            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors group px-6 py-2"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-bold uppercase tracking-widest">Return to Base Command</span>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
